import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CELL_WIDTH = 80;
const CELL_HEIGHT = 32;
const LABEL_LEFT_WIDTH = 44;
const HEADER_HEIGHT = 36;
const MARGIN = { top: 8, right: 16, bottom: 16, left: 8 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  scrollContainer: {
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '220px',
  },
  legend: {
    marginTop: '12px',
    display: 'flex',
    gap: '12px',
    fontSize: '11px',
    color: '#9ca3af',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  legendSwatch: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    verticalAlign: 'middle',
    marginRight: '4px',
  },
};

export default function PriceHeatmapGrid({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.states || data.states.length === 0 || !data.fields || data.fields.length === 0) return;

    const { states, fields, fieldLabels, values, ranges } = data;

    // Sort states by total average price descending
    const sortedStates = [...states].sort((a, b) => {
      const avgA = fields.reduce((sum, f) => {
        const v = values[a]?.[f];
        return v != null ? sum + v : sum;
      }, 0);
      const avgB = fields.reduce((sum, f) => {
        const v = values[b]?.[f];
        return v != null ? sum + v : sum;
      }, 0);
      return avgB - avgA;
    });

    // Build color scales per field
    const colorScales = {};
    fields.forEach((f) => {
      const range = ranges[f];
      if (range) {
        colorScales[f] = d3.scaleSequential()
          .domain([range.min, range.max])
          .interpolator(d3.interpolateBlues);
      }
    });

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const gridLeft = MARGIN.left + LABEL_LEFT_WIDTH;
    const gridTop = MARGIN.top + HEADER_HEIGHT;
    const width = gridLeft + fields.length * CELL_WIDTH + MARGIN.right;
    const height = gridTop + sortedStates.length * CELL_HEIGHT + MARGIN.bottom;

    svg
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMinYMin meet');

    const g = svg.append('g');

    // ---------------------------------------------------------------
    // Field column headers
    // ---------------------------------------------------------------
    fields.forEach((f, ci) => {
      const label = (fieldLabels && fieldLabels[f]) || f;
      g.append('text')
        .attr('x', gridLeft + ci * CELL_WIDTH + CELL_WIDTH / 2)
        .attr('y', gridTop - 10)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('font-weight', '600')
        .style('fill', '#6b7280')
        .style('text-transform', 'uppercase')
        .text(label);
    });

    // ---------------------------------------------------------------
    // State row labels + heatmap cells
    // ---------------------------------------------------------------
    sortedStates.forEach((state, ri) => {
      const y = gridTop + ri * CELL_HEIGHT;

      // State label
      g.append('text')
        .attr('x', MARGIN.left + LABEL_LEFT_WIDTH - 8)
        .attr('y', y + CELL_HEIGHT / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '12px')
        .style('font-weight', '600')
        .style('fill', '#374151')
        .text(state);

      // Cells for each field
      fields.forEach((f, ci) => {
        const val = values[state]?.[f];
        const x = gridLeft + ci * CELL_WIDTH;
        const isNull = val == null;
        const range = ranges[f];

        // Cell rectangle
        let fillColor;
        if (isNull) {
          fillColor = '#f3f4f6';
        } else if (colorScales[f]) {
          fillColor = colorScales[f](val);
        } else {
          fillColor = '#eff6ff';
        }

        g.append('rect')
          .attr('x', x + 1)
          .attr('y', y + 1)
          .attr('width', CELL_WIDTH - 2)
          .attr('height', CELL_HEIGHT - 2)
          .attr('fill', fillColor)
          .attr('rx', 3)
          .attr('stroke', '#e5e7eb')
          .attr('stroke-width', 0.5)
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            let percentile = null;
            if (!isNull && range) {
              const span = range.max - range.min;
              percentile = span > 0 ? Math.round(((val - range.min) / span) * 100) : 50;
              percentile = Math.max(0, Math.min(100, percentile));
            }
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              state,
              field: (fieldLabels && fieldLabels[f]) || f,
              value: val,
              percentile,
            });
          })
          .on('mouseleave', () => {
            setTooltip(null);
          });

        // Cell text
        const textColor = isNull
          ? '#9ca3af'
          : d3.lab(fillColor).l < 55
          ? '#ffffff'
          : '#1e3a5f';

        g.append('text')
          .attr('x', x + CELL_WIDTH / 2)
          .attr('y', y + CELL_HEIGHT / 2)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .style('font-size', '12px')
          .style('font-weight', '600')
          .style('fill', textColor)
          .style('pointer-events', 'none')
          .text(isNull ? 'N/A' : `$${val.toFixed(2)}`);
      });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || !data.states || data.states.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>State × Field Price Matrix</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No price matrix data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card} ref={containerRef}>
      <div style={styles.title}>State × Field Price Matrix</div>
      <div style={styles.subtitle}>
        Average prices by state and service type — darker = higher price
      </div>
      <div style={styles.scrollContainer}>
        <svg ref={svgRef} />
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#eff6ff' }} />
          Low
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#6baddf' }} />
          Mid
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#1e40af' }} />
          High
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#f3f4f6', border: '1px solid #e5e7eb' }} />
          N/A
        </span>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state} — {tooltip.field}
          </div>
          {tooltip.value != null ? (
            <>
              <div style={{ color: '#374151', marginBottom: '2px' }}>
                Price: <strong>${tooltip.value.toFixed(2)}</strong>
              </div>
              {tooltip.percentile != null && (
                <div style={{ color: '#6b7280' }}>
                  Percentile: {tooltip.percentile}th
                </div>
              )}
            </>
          ) : (
            <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not available</div>
          )}
        </div>
      )}
    </div>
  );
}
